// El parte diario que el muchacho le manda a Gabriel por WhatsApp.
//
// ── Por qué existe ──
//
// Gabriel no entra al programa: entra el muchacho que registra la leche y las
// cuajadas, y después le pasa los promedios por teléfono. Eso se hacía a mano,
// sacando los números de la pantalla y escribiéndolos en el chat — y un número
// copiado a mano todos los días, tarde o temprano, sale distinto del que está
// en el programa.
//
// ── Por qué texto y no un archivo ──
//
// El enlace de WhatsApp solo lleva TEXTO. Un PDF hay que adjuntarlo a mano:
// imprimir, guardar, buscar el archivo, arrastrarlo. Son cuatro pasos, todos
// los días, para algo que se lee en diez segundos. Eso se hace tres días y se
// abandona; el parte tiene que costar un toque o no se manda.
//
// El papel formal sigue existiendo aparte, para el que quiera guardarlo.
//
// ── Las cuentas no se rehacen aquí ──
//
// Se usan `lecturaDeRuta` y `compararRutas`, las mismas de la pantalla. Si este
// archivo sacara su propio promedio, el mensaje y la pantalla dirían cosas
// distintas del mismo día — y el que queda mal es quien mandó el mensaje.

import { lecturaDeRuta, compararRutas, calificar } from './ruta';
import { promedio, conComa, peso, fecha } from './formato';

// «21 de agosto», sin el año: el parte es de hoy y el año sobra en un mensaje
// que se lee el mismo día. En el papel sí va completo.
const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio',
               'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

export function diaLargo(f) {
  const d = f instanceof Date ? f : new Date(String(f).length <= 10 ? f + 'T00:00:00' : f);
  if (isNaN(d.getTime())) return '';
  return `${d.getDate()} de ${MESES[d.getMonth()]}`;
}

const MEDALLA = ['🥇', '🥈', '🥉'];

// Los totales del día, contando solo lo que de verdad se puede juzgar para el
// cumplimiento. Es la misma separación que hace la pantalla: una cuajada sin
// rendimiento pactado suma a la producción pero no a la nota.
export function totalesDelDia(filas) {
  // Una lista o nada. El endpoint devuelve `{ centros: [...] }` y pasarle el
  // objeto entero reventaba la pantalla ENTERA y la dejaba en blanco: un
  // `.reduce` sobre algo que no es lista tumba todo el árbol de React.
  //
  // El sitio de llamada ya saca `centros`, pero eso no basta: un dato con otra
  // forma no puede costar la pantalla completa, porque quien la ve no tiene
  // forma de saber qué pasó ni a qué volver.
  const lista = Array.isArray(filas) ? filas : [];
  const t = lista.reduce((a, r) => ({
    litros: a.litros + (Number(r.litros) || 0),
    kilos: a.kilos + (Number(r.kilos) || 0),
    esperados: a.esperados + (Number(r.kilos_esperados) || 0),
    medibles: a.medibles + (Number(r.kilos_medibles) || 0),
    cuajadas: a.cuajadas + (Number(r.cuajadas) || 0),
  }), { litros: 0, kilos: 0, esperados: 0, medibles: 0, cuajadas: 0 });

  const lectura = lecturaDeRuta({
    litros: t.litros, kilos: t.kilos,
    kilos_esperados: t.esperados, kilos_medibles: t.medibles,
  });
  return { ...t, lectura };
}

// El parte, listo para pegar en WhatsApp.
//
// `filas` es lo que devuelve /api/centros/rendimiento para UN día.
export function parteDelDia(filas, opciones = {}) {
  const lista = Array.isArray(filas) ? filas : [];
  const negocio = (opciones.negocio || '').trim();
  const dia = opciones.dia;
  const t = totalesDelDia(lista);

  // Sin producción no se manda un parte vacío que diga «0 kilos»: eso se lee
  // como que el programa falló, no como que ese día no se cuajó.
  if (t.cuajadas === 0 || t.kilos <= 0) return null;

  const lineas = [];
  if (negocio) lineas.push(`*${negocio}*`);
  lineas.push(`Producción del ${diaLargo(dia)}`);
  lineas.push('');

  // EL NÚMERO PRIMERO. Es el que Gabriel pide, y va antes que cualquier
  // desglose: si tiene que bajar tres renglones para encontrarlo, el parte le
  // está haciendo trabajo a él en vez de ahorrárselo.
  lineas.push(`*Promedio: ${promedio(t.lectura.porTina)} kg por tina*`);
  lineas.push(`${peso(t.litros, 'L')} · ${conComa(t.lectura.tinas)} tinas · ` +
              `${peso(t.kilos, 'kg')} de queso`);
  if (t.lectura.cumple !== null) {
    lineas.push(`Cumplimiento: ${conComa(t.lectura.cumple)}% ` +
                `— ${calificar(t.lectura.cumple).nombre.toLowerCase()}`);
  }

  // El mismo orden de la pantalla: por cumplimiento, no por kilos por tina. A
  // la ruta que le toca más búfala saca más kilos por tina sin haber hecho
  // nada mejor, y un parte que la corone premia la suerte del reparto.
  const enOrden = compararRutas(lista).filter(x => x.lectura);

  if (enOrden.length > 1) {
    lineas.push('');
    lineas.push('*Por ruta*');
    enOrden.forEach((r, i) => {
      const L = r.lectura;
      const nota = L.cumple !== null ? ` · ${conComa(L.cumple)}%` : '';
      // La medalla solo donde se pudo medir. Un 🥇 a una ruta sin nota sería
      // un premio sorteado.
      // Del cuarto en adelante va el PUESTO, no un punto suelto.
      //
      // Con seis rutas salían tres medallas y tres puntos, y eso se lee a medio
      // hacer: parece que a las últimas les faltó algo. El número dice lo mismo
      // que la medalla —en qué puesto va— y la lista se sigue leyendo como lo
      // que es, una clasificación.
      const marca = L.cumple === null ? '·'
        : (calificar(L.cumple).peso <= 2 ? '⚠️' : (MEDALLA[i] || `${i + 1}.`));
      lineas.push(`${marca} ${r.nombre}: ${promedio(L.porTina)} kg/tina${nota}`);
    });
  }

  // LA FRASE QUE HACE QUE EL PARTE SIRVA PARA ALGO.
  //
  // Una lista de números no mueve a nadie. Nombrar la ruta que se quedó corta
  // —y decir cuántos kilos son— es lo que hace que Gabriel llame hoy y no el
  // mes que viene, cuando ya no se puede revisar esa leche.
  const flojas = enOrden.filter(r => r.lectura.cumple !== null &&
                                     calificar(r.lectura.cumple).peso <= 2);
  if (flojas.length > 0) {
    lineas.push('');
    for (const r of flojas) {
      const L = r.lectura;
      const faltaron = Math.round((Number(r.kilos_esperados) || 0) -
                                  (Number(r.kilos_medibles) || 0));
      lineas.push(`⚠️ *${r.nombre}* quedó en ${conComa(L.cumple)}%` +
                  (faltaron > 0 ? `: faltaron ${peso(faltaron, 'kg')}.` : '.'));
    }
  }

  // Las que no cuajaron. Se dicen porque su ausencia es información: si LA FE
  // lleva tres días sin aparecer, alguien tiene que preguntarse por qué.
  const quietas = lista.filter(r => !(Number(r.cuajadas) > 0)).map(r => r.nombre);
  if (quietas.length > 0 && enOrden.length > 0) {
    lineas.push('');
    lineas.push(`_Sin cuajar hoy: ${quietas.join(', ')}._`);
  }

  if (opciones.cierre) {
    lineas.push('');
    lineas.push(opciones.cierre);
  }
  return lineas.join('\n');
}

// El teléfono, como lo entiende wa.me. Diez dígitos sueltos son de Colombia,
// que es como se anotan aquí.
export function aWhatsApp(telefono) {
  const d = String(telefono || '').replace(/\D/g, '');
  if (d.length === 10) return `57${d}`;
  if (d.length >= 11) return d;
  return null;
}

// El enlace que abre WhatsApp con el parte escrito. NO manda nada solo: se abre
// la conversación y el muchacho le da a enviar. Un parte con una cifra
// equivocada, mandado solo, no se puede recoger.
//
// Sin teléfono devuelve el enlace pelado: WhatsApp abre con el texto listo y
// él elige a quién. Es mejor que no poder mandarlo por no tener el número
// anotado en Ajustes.
export function enlaceDeWhatsApp(telefono, mensaje) {
  if (!mensaje) return null;
  const numero = aWhatsApp(telefono);
  const texto = encodeURIComponent(mensaje);
  return numero ? `https://wa.me/${numero}?text=${texto}`
                : `https://wa.me/?text=${texto}`;
}

// Para el papel: el día con año, que un parte guardado se vuelve a mirar meses
// después y «21 de agosto» a secas no dice de cuál año.
export function diaDelPapel(f) {
  return fecha(f);
}
